import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import {
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
  Avatar,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import Link from "@mui/material/Link";
import "../../MetaEntities.css";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EditGeoModal from "../GeoModal/EditGeoModal";
import {
  setPage,
  setRowsPerPage,
  setSelectedRows,
  setSort,
} from "../../../../../features/metaEntitiesSlice";
import { useSelector } from "react-redux";
import store from "../../../../../store";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { formatDate } from '../../../../../utils/helpers';

interface Props {
  tableHeadings: { [key: string]: string };
  loading: any;
  oauthTokenHeader: any;
  selectedMetaGeos: any;
  setSelectedMetaGeos: any;
  MetaGeos: any;
  setMetaGeos: any;
  selectedTerritory: any;
  setSelectedTerritory: any;
  selectedTimezone: any;
  setSelectedTimezone: any;
}

const GeoTable = ({ tableHeadings, oauthTokenHeader, setMetaGeos }: Props) => {
  const {
    selectedEntityType,
    metaEntityList,
    sort,
    page,
    rowsPerPage,
    geoAssociatedTerritoryFilter,
    geoTimezoneFilter,
  } = useSelector((state: any) => state?.metaEntity);
  const dispatch = useDispatch();
  const [sortedData, setSortedData] = React.useState<any[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const headCells = Object.entries(tableHeadings);

  const filteredGeoData = sortedData?.filter((geo: any) => {
    const statusDeviceTypeName =
      selectedEntityType && selectedEntityType.length !== 0
        ? selectedEntityType.includes(geo?.name)
        : true; // If no Asset is selected, treat it as a match
    const statusTerritory =
      geoAssociatedTerritoryFilter && geoAssociatedTerritoryFilter.length !== 0
        ? geoAssociatedTerritoryFilter.includes(geo?.territory_name)
        : true;
    const statusTimezone =
      geoTimezoneFilter && geoTimezoneFilter.length !== 0
        ? geoTimezoneFilter.includes(geo?.timezone)
        : true;
    return statusDeviceTypeName && statusTerritory && statusTimezone;
  });

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable
    dispatch(
      setSort({
        keytoSort: header.KEY,
        // Toggle sort direction if the same column is clicked, otherwise default to ascending
        direction:
          header.KEY === sort.keytoSort && sort.direction === "asc"
            ? "desc"
            : "asc",
      })
    );
  };

  const tableheaders = [
    { id: 0, KEY: "name", label: "Geo Name", sortable: true },
    {
      id: 1,
      KEY: "territory_name",
      label: "Associated Territory",
      sortable: true,
    },
    { id: 2, KEY: "timezone", label: "Time Zone", sortable: true },
    { id: 3, KEY: "created", label: "Created on", sortable: true },
    { id: 4, KEY: "created_by", label: "Created By", sortable: true },
    { id: 5, KEY: "modified", label: "Last edited", sortable: true },
    { id: 6, KEY: "modified_by", label: "Edited By", sortable: true },
  ];

  const getSortedData = React.useCallback(
    (arrayToSort: any[]) => {
      if (!sort.keytoSort || !sort.direction) {
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sort.keytoSort;

        if (keytoSort === undefined) {
          return 0;
        }

        const aValue = a[keytoSort];
        const bValue = b[keytoSort];

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sort.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
        } else {
          return sort.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sort]
  );

  const sortedRows = getSortedData(filteredGeoData || []);
  const paginatedGeoData = sortedRows?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const [openEditModal, setopenModal] = useState(false);
  const [geoName, setGeoName] = useState(null);
  const [territoryUID, setTerritoryUID] = useState("");
  const [timeZone, setTimeZone] = useState(null);
  const [selectedShareuid, setSelectedShareuid] = useState(null);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);

  const handleopenEditModal = (row: any) => {
    setopenModal(true);
    setGeoName(row?.name);
    setTerritoryUID(row?.territory_shared_uid);
    setTimeZone(row?.timezone);
    setSelectedShareuid(row?.shared_uid);
  };
  const handlecloseEditModal = () => {
    setopenModal(false);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    store.dispatch(setPage(newPage));

    setSearchParams((prevParams) => {
      const updatedParams = new URLSearchParams(prevParams);
      updatedParams.set("page", newPage.toString());
      return updatedParams;
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    store.dispatch(setSelectedRows([]));
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    const sortedRows = getSortedData(metaEntityList || []);
    setSortedData(sortedRows);
    //eslint-disable-next-line
  }, [metaEntityList, getSortedData]);
  useEffect(() => {
    if (page !== 0) {
      store.dispatch(setPage(0));
    }
    // eslint-disable-next-line
  }, [selectedEntityType, geoAssociatedTerritoryFilter, geoTimezoneFilter]);

  useEffect(() => {
    const pageFromUrl = Number(searchParams.get("page")); // Convert to number
    store.dispatch(setPage(isNaN(pageFromUrl) ? 0 : pageFromUrl)); // Ensure it defaults correctly
  }, [searchParams]);

  return (
    <>
      <TableContainer
        className="PageScantablewrap"
        component={Paper}
        sx={{ maxHeight: 800 }}
      >
        <Table aria-label="collapsible table" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tableheaders.map((header: any) => (
                <TableCell key={header.id} align="left">
                  <TableSortLabel
                    active={sort.keytoSort === header.KEY}
                    direction={
                      sort.keytoSort === header.KEY ? sort.direction : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedGeoData.length > 0 &&
              paginatedGeoData?.map((row, rowIndex) => (
                <React.Fragment key={rowIndex}>
                  <TableRow
                    sx={{
                      "& > *": {
                        borderBottom:
                          "1px solid rgba(224, 224, 224, 1) !important",
                      },
                    }}
                  >
                    {headCells.map(([key], cellIndex) => (
                      <TableCell key={cellIndex} align="left">
                        {key === "name" ? (
                          <>
                            <Link href="#" color="secondary" sx={{ mr: 1 }}>
                              <EditOutlinedIcon
                                data-tracker="234"
                                onClick={() => handleopenEditModal(row)}
                                fontSize="small"
                              />
                            </Link>
                            {row[key] ? row[key] : "N/A"}
                          </>
                        ) : key === "territory_name" ? (
                          <> {row[key] ? row[key] : "N/A"}</>
                        ) : key === "timezone" ? (
                          <> {row[key] ? row[key] : "N/A"}</>
                        ) : key === "created" ? (
                          <> {row[key] ? formatDate(row[key]) : "N/A"}</>
                        ) : key === "created_by" ? (
                          <Stack
                            flexDirection={"row"}
                            alignItems={"center"}
                            gap={1}
                          >
                            <Avatar
                              alt={row[key] ? row[key] : "N/A"}
                              src="#"
                              sx={{ width: 24, height: 24 }}
                            />
                            {row[key] ? row[key] : "N/A"}
                          </Stack>
                        ) : key === "modified" ? (
                          <>{row[key] ? formatDate(row[key]) : "N/A"}</>
                        ) : key === "modified_by" ? (
                          <Stack
                            flexDirection={"row"}
                            alignItems={"center"}
                            gap={1}
                          >
                            <Avatar
                              alt={row[key] ? row[key] : "N/A"}
                              src="#"
                              sx={{ width: 24, height: 24 }}
                            />
                            {row[key] ? row[key] : "N/A"}
                          </Stack>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </React.Fragment>
              ))}
          </TableBody>
        </Table>
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={sortedRows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingLeft: "16px",
              },
            }}
          />
          <Pagination
            count={Math.ceil(filteredGeoData?.length / rowsPerPage)}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            onChange={(_, value) => handleChangePage(null, value - 1)}
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
            page={page + 1}
          />
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={sortedRows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginRight: "15px",
              },
            }}
          />
        </Stack>
        <EditGeoModal
          open={openEditModal}
          handleClose={handlecloseEditModal}
          geoName={geoName}
          territoryUID={territoryUID}
          timeZone={timeZone}
          selectedShareuid={selectedShareuid}
          oauthTokenHeader={oauthTokenHeader}
          setMetaGeos={setMetaGeos}
          setGeoName={setGeoName}
          setTerritoryUID={setTerritoryUID}
          setTimeZone={setTimeZone}
          setSelectedShareuid={setSelectedShareuid}
          selectedRowData={selectedRowData}
          setSelectedRowData={setSelectedRowData}
        />
      </TableContainer>
    </>
  );
};

export default GeoTable;
